/**
 * PATH: packages/ui/src/kanban/components/KanbanCardQuickActionPickers.tsx
 * FILE_ID: UI-KANBAN-CARD-QUICKACTION-PICKERS-001
 * PURPOSE: The portaled picker popovers (priority / due / assign / move) that
 *   the card-face quick-action rail opens. Pure presentational — the parent
 *   (KanbanCardQuickActions) owns the open/busy state + the `run` dispatcher.
 * WHY:
 *   FEAT-2026-06-20-KANBAN-CARD-ACTION-RAIL. Split out of KanbanCardQuickActions
 *   so the rail (buttons + state) and the pickers (option lists) each stay
 *   under the 300-line target (05_agent_operating_system §1.7) and read as one
 *   responsibility apiece. Option sets mirror KanbanContextMenu exactly so the
 *   inline picker and the right-click submenu offer identical choices.
 * FLOW:
 *   ┌──────────┐   open.kind   ┌────────────────────────┐   pick   ┌─────────┐
 *   │ open st. │ ───────────▶  │ <picker popover body>   │ ──────▶ │ run(fn) │
 *   └──────────┘               └────────────────────────┘         └─────────┘
 * DEPENDENCIES:
 *   - ./PeoplePicker: shared assign list (parity with the right-click submenu).
 *   - ./KanbanCardQuickActionsPopover: portaled, clipping-safe picker shell.
 * RELATED:
 *   - ./KanbanCardQuickActions.tsx: the only consumer (owns the state).
 */
import { Check, Loader2 } from 'lucide-react'

import type { Card, Lane } from '../types'
import type { KanbanCardQuickActionHandlers } from './KanbanCard.types'
import { PeoplePicker, kanbanMemberToPerson } from './PeoplePicker'
import { KanbanCardComposePicker } from './KanbanCardComposePicker'
import { KanbanCardAllocateBoards } from './KanbanCardAllocateBoards'
import { KanbanCardQuickActionsPopover } from './KanbanCardQuickActionsPopover'
import { SmartDateInput } from './DateField'
import { actionHorizonPresets } from './board-view/swimlaneGrouping'
import type { TriggerRect } from './placeBesideTrigger'

export type QuickActionPickerKind =
  | 'priority'
  | 'due'
  | 'action'
  | 'assign'
  | 'move'
  | 'allocate'
  | 'compose'
export interface QuickActionOpenState {
  kind: QuickActionPickerKind
  /** Trigger icon rect — popover opens beside it (not over the rail). */
  trigger: TriggerRect
}

const ROW =
  'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-neutral-100 disabled:opacity-50 dark:hover:bg-neutral-900'

const PRIORITY_OPTS = [
  { value: 3, label: 'Urgent', color: 'bg-red-500' },
  { value: 2, label: 'High', color: 'bg-amber-500' },
  { value: 1, label: 'Normal', color: 'bg-blue-500' },
  { value: 0, label: 'Low (clear)', color: 'bg-neutral-400' },
] as const

// FEAT-2026-07-02-KANBAN-DUE-PICKER-LADDER, operator: "have more options".
// The due picker now offers the SAME horizon ladder as the action-date preset
// submenu (Today · Tomorrow · This week · Next week · In 2/3/4 weeks, from
// `actionHorizonPresets`), so the quick rail matches the Split band ladder the
// operator navigates by, instead of a lean 3-row set.

const _WD_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const _MON_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/** f(iso YYYY-MM-DD) -> "Thu 9 Jul" — the readable hint beside each preset
 *  (operator prefers spelled-out dates on card faces; raw ISO reads as noise). */
function isoToShortLabel(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return iso
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return `${_WD_SHORT[d.getDay()]} ${d.getDate()} ${_MON_SHORT[d.getMonth()]}`
}

export interface KanbanCardQuickActionPickersProps {
  open: QuickActionOpenState
  card: Card
  busy: boolean
  assignBusyId: number | null
  /** Pick-and-close dispatcher for the one-shot pickers (priority/due/move). */
  run: (fn: () => Promise<unknown> | void) => void
  /** Assign toggle that owns its own busy id (stays open for multi-assign). */
  onAssignToggle: (userId: number, willSelect: boolean) => void | Promise<void>
  /** FEAT-2026-06-23-KANBAN-CARD-COMPOSE — fired (card already bound by the
   *  parent) with the chosen recipient addresses; the parent closes the
   *  popover + opens the host composer. */
  onComposeEmail?: (recipients: string[]) => void
  onClose: () => void
  /** Hover-bridge hooks (FIX-2026-06-23-KANBAN-CARD-PICKER-DISMISS): the parent
   *  shares one close-timer across the rail + this portaled popover, so the
   *  picker auto-dismisses when the pointer leaves both. Entering the popover
   *  cancels the pending close; leaving it re-arms. Optional so standalone
   *  callers (none today) keep the plain outside-click dismissal. */
  onHoverIn?: () => void
  onHoverOut?: () => void
  handlers: Pick<
    KanbanCardQuickActionHandlers,
    | 'onSetPriority'
    | 'onSetDueDate'
    | 'onSetActionDate'
    | 'onAssign'
    | 'onMoveTo'
    | 'onAllocateToLane'
    | 'onAllocateToBoard'
    | 'onLoadBoards'
    | 'onLoadBoardLanes'
    | 'onMoveToBoard'
    | 'currentBoardId'
    | 'members'
    | 'lanes'
  >
}

/** Renders the single picker popover that matches `open.kind`. */
export function KanbanCardQuickActionPickers({
  open,
  card,
  busy,
  assignBusyId,
  run,
  onAssignToggle,
  onComposeEmail,
  onClose,
  onHoverIn,
  onHoverOut,
  handlers,
}: KanbanCardQuickActionPickersProps) {
  const {
    onSetPriority,
    onSetDueDate,
    onSetActionDate,
    onAssign,
    onMoveTo,
    onAllocateToLane,
    onAllocateToBoard,
    onLoadBoards,
    onLoadBoardLanes,
    onMoveToBoard,
    currentBoardId,
    members,
    lanes,
  } = handlers
  // Forwarded to every popover so the hover-bridge covers whichever picker is open.
  const hoverProps = { onHoverIn, onHoverOut }

  if (open.kind === 'priority' && onSetPriority) {
    return (
      <KanbanCardQuickActionsPopover trigger={open.trigger} label="Set priority" onClose={onClose} widthClassName="w-44" {...hoverProps}>
        {PRIORITY_OPTS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            disabled={busy}
            data-testid={`kanban-quick-priority-${opt.value}`}
            onClick={() => run(() => onSetPriority(card, opt.value))}
            className={ROW}
          >
            <span className="inline-flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${opt.color}`} aria-hidden="true" />
              {opt.label}
            </span>
            {card.priority === opt.value && <Check className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />}
          </button>
        ))}
      </KanbanCardQuickActionsPopover>
    )
  }

  if (open.kind === 'due' && onSetDueDate) {
    return (
      <KanbanCardQuickActionsPopover trigger={open.trigger} label="Set due date" onClose={onClose} widthClassName="w-52" {...hoverProps}>
        {actionHorizonPresets(new Date()).map((opt) => (
          <button
            key={opt.label}
            type="button"
            disabled={busy}
            data-testid={`kanban-quick-due-${opt.label.replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => run(() => onSetDueDate(card, opt.iso))}
            className={ROW}
          >
            <span>{opt.label}</span>
            <span className="text-[10px] text-neutral-400">{isoToShortLabel(opt.iso)}</span>
          </button>
        ))}
        {/* ARC-6 (U05): custom date is the shared SmartDateInput — type
            "12/8", "week 6", "tomorrow" instead of the strict native
            widget. Renders DD/MM/YYYY; calendar popover stays as the
            secondary affordance. */}
        <div
          className="flex items-center gap-2 border-t border-neutral-100 px-3 py-2 dark:border-zinc-800"
          data-testid="kanban-quick-due-custom"
        >
          <SmartDateInput
            ariaLabel="Custom due date"
            placeholder="12/8 · week 6 · tomorrow"
            value={card.due_date ? card.due_date.slice(0, 10) : null}
            disabled={busy}
            onChange={(iso) => {
              if (iso !== (card.due_date?.slice(0, 10) ?? null)) {
                run(() => onSetDueDate(card, iso))
              }
            }}
            className="w-full rounded border border-neutral-200 bg-neutral-50 px-2 py-1 text-xs text-neutral-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:border-zinc-800 dark:bg-zinc-900 dark:text-neutral-200"
          />
        </div>
        {card.due_date && (
          <button
            type="button"
            disabled={busy}
            data-testid="kanban-quick-due-clear"
            onClick={() => run(() => onSetDueDate(card, null))}
            className="flex w-full items-center gap-2 border-t border-neutral-100 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-zinc-800 dark:text-red-400 dark:hover:bg-red-950/30"
          >
            Clear due date
          </button>
        )}
      </KanbanCardQuickActionsPopover>
    )
  }

  // Same ladder as the context-menu "Set action date…" — writes action_date,
  // not due_date (BUG: calendar button previously only wired due).
  if (open.kind === 'action' && onSetActionDate) {
    return (
      <KanbanCardQuickActionsPopover
        trigger={open.trigger}
        label="Set action date"
        onClose={onClose}
        widthClassName="w-52"
        {...hoverProps}
      >
        {actionHorizonPresets(new Date()).map((opt) => (
          <button
            key={opt.label}
            type="button"
            disabled={busy}
            data-testid={`kanban-quick-action-${opt.label.replace(/\s+/g, '-').toLowerCase()}`}
            onClick={() => run(() => onSetActionDate(card, opt.iso))}
            className={ROW}
          >
            <span>{opt.label}</span>
            <span className="text-[10px] text-neutral-400">{isoToShortLabel(opt.iso)}</span>
          </button>
        ))}
        <div
          className="flex items-center gap-2 border-t border-neutral-100 px-3 py-2 dark:border-zinc-800"
          data-testid="kanban-quick-action-custom"
        >
          <SmartDateInput
            ariaLabel="Custom action date"
            placeholder="12/8 · week 6 · tomorrow"
            value={card.action_date ? card.action_date.slice(0, 10) : null}
            disabled={busy}
            onChange={(iso) => {
              if (iso !== (card.action_date?.slice(0, 10) ?? null)) {
                run(() => onSetActionDate(card, iso))
              }
            }}
            className="w-full rounded border border-neutral-200 bg-neutral-50 px-2 py-1 text-xs text-neutral-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:border-zinc-800 dark:bg-zinc-900 dark:text-neutral-200"
          />
        </div>
        {card.action_date && (
          <button
            type="button"
            disabled={busy}
            data-testid="kanban-quick-action-clear"
            onClick={() => run(() => onSetActionDate(card, null))}
            className="flex w-full items-center gap-2 border-t border-neutral-100 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-zinc-800 dark:text-red-400 dark:hover:bg-red-950/30"
          >
            Clear action date
          </button>
        )}
      </KanbanCardQuickActionsPopover>
    )
  }

  if (open.kind === 'assign' && onAssign && (members?.length ?? 0) > 0) {
    return (
      <KanbanCardQuickActionsPopover trigger={open.trigger} label="Assign members" onClose={onClose} widthClassName="w-64" {...hoverProps}>
        <PeoplePicker
          people={(members ?? []).map(kanbanMemberToPerson)}
          selectedIds={new Set<number>(card.assigned_user_ids ?? [])}
          busyId={assignBusyId}
          hint="Click to assign / unassign. Move away to close."
          onToggle={onAssignToggle}
        />
      </KanbanCardQuickActionsPopover>
    )
  }

  if (open.kind === 'compose' && onComposeEmail) {
    return (
      <KanbanCardQuickActionsPopover
        trigger={open.trigger}
        label="Send card as email"
        onClose={onClose}
        widthClassName="w-72"
        {...hoverProps}
      >
        <KanbanCardComposePicker
          members={members ?? []}
          onSend={(recipients) => {
            onComposeEmail(recipients)
            onClose()
          }}
        />
      </KanbanCardQuickActionsPopover>
    )
  }

  if (open.kind === 'move' && onMoveTo && (lanes?.length ?? 0) > 0) {
    return (
      <KanbanCardQuickActionsPopover trigger={open.trigger} label="Move to lane" onClose={onClose} widthClassName="w-56" {...hoverProps}>
        {(lanes ?? []).map((lane: Lane) => {
          const current = lane.id === card.lane
          return (
            <button
              key={lane.id}
              type="button"
              disabled={busy || current}
              data-testid={`kanban-quick-move-${lane.id}`}
              onClick={() => run(() => onMoveTo(card, lane))}
              className={ROW}
            >
              <span className="truncate">{lane.name}</span>
              {current ? (
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-neutral-400">Current</span>
              ) : busy ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-neutral-400" />
              ) : null}
            </button>
          )
        })}
      </KanbanCardQuickActionsPopover>
    )
  }

  if (open.kind === 'allocate' && (onAllocateToLane || onAllocateToBoard || onMoveToBoard)) {
    return (
      <KanbanCardQuickActionsPopover trigger={open.trigger} label="Allocate" onClose={onClose} widthClassName="w-64" {...hoverProps}>
        {(lanes ?? []).map((lane: Lane) => {
          const current = lane.id === card.lane
          return (
            <button
              key={lane.id}
              type="button"
              disabled={busy || current || !onAllocateToLane}
              data-testid={`kanban-quick-allocate-${lane.id}`}
              onClick={() => onAllocateToLane && run(() => onAllocateToLane(card, lane))}
              className={ROW}
            >
              <span className="truncate">{lane.name}</span>
              {current ? (
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-neutral-400">Here</span>
              ) : busy ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-neutral-400" />
              ) : null}
            </button>
          )
        })}
        <KanbanCardAllocateBoards
          card={card}
          busy={busy}
          currentBoardId={currentBoardId ?? card.home_board_id}
          onMoveToBoard={onMoveToBoard}
          onLoadBoards={onLoadBoards}
          run={run}
        />
      </KanbanCardQuickActionsPopover>
    )
  }

  return null
}
